import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Lock, Mail, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { submitLead } from "../lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Unlock = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("rolefinder_result")) {
      navigate("/assessment");
    }
  }, [navigate]);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit =
    name.trim().length >= 2 && emailValid && consent && !busy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    try {
      setBusy(true);
      const result = JSON.parse(
        sessionStorage.getItem("rolefinder_result") || "{}",
      ).result;
      const payload = JSON.parse(
        sessionStorage.getItem("rolefinder_payload") || "{}",
      );
      const ml = result?.mailerlite || {};
      const hero =
        result?.co_primary_roles?.[0] || result?.primary || result?.top_overall;

      await submitLead({
        name: name.trim(),
        email: email.trim(),
        consent,
        technical_skills: payload?.technical_skills || [],
        soft_skills: payload?.soft_skills || [],
        total_years_experience: payload?.total_years_experience || 0,
        local_band: payload?.local_band || "none",
        top_role: ml.top_role || hero?.role_name || null,
        secondary_role: ml.secondary_role || null,
        fit_score: ml.fit_score ?? hero?.skills_match ?? hero?.final_score ?? null,
        recommendation: hero?.recommendation || null,
        top_gaps: ml.top_gaps || hero?.top_gaps || [],
        next_skill_to_add: ml.next_skill_to_add || null,
      });
      sessionStorage.setItem("rolefinder_unlocked", "1");
      toast.success("Unlocked!", {
        description: "Loading your full report…",
        duration: 4000,
      });
      navigate("/results");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't save your details. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Header />
      <main className="flex-1 px-5 sm:px-6 lg:px-10 py-10 lg:py-14">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 lg:p-10 shadow-xl shadow-slate-900/5">
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">
              <Lock size={12} /> One step left to unlock your report
            </div>

            <h1
              data-testid="unlock-title"
              className="mt-3 font-display text-3xl sm:text-4xl font-black text-slate-900 leading-tight"
            >
              <span className="inline-flex items-center gap-2">
                <span aria-hidden="true">🎉</span>
                Your results are ready.
              </span>
            </h1>
            <p
              data-testid="unlock-teaser-copy"
              className="mt-4 text-slate-700 text-base sm:text-[17px] leading-relaxed"
            >
              You&rsquo;re a strong match for an in-demand role &mdash; and
              you&rsquo;re closer than you think. Enter your details to unlock
              your best-fit role, your skills-match score, and the one skill
              standing between you and it.
            </p>

            <div
              data-testid="unlock-no-spoilers"
              className="mt-5 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-center gap-3 text-sm text-amber-900"
            >
              <Sparkles size={14} className="shrink-0" />
              <span>
                <b>No spoilers yet.</b> Your role, score and next skill stay
                hidden until you unlock.
              </span>
            </div>

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <div>
                <label
                  htmlFor="lead-name"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Full name
                </label>
                <Input
                  id="lead-name"
                  data-testid="lead-name-input"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Priya Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 h-11 bg-white"
                />
              </div>
              <div>
                <label
                  htmlFor="lead-email"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Email
                </label>
                <div className="relative mt-1.5">
                  <Mail
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <Input
                    id="lead-email"
                    data-testid="lead-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11 bg-white"
                  />
                </div>
                {touched && email && !emailValid && (
                  <p
                    data-testid="lead-email-error"
                    className="mt-1.5 text-xs text-rose-700 font-medium"
                  >
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              <label
                data-testid="lead-consent-row"
                className={`flex items-start gap-3 cursor-pointer pt-1 rounded-md p-2 -m-2 ${
                  touched && !consent
                    ? "ring-2 ring-rose-200 bg-rose-50/50"
                    : ""
                }`}
              >
                <input
                  data-testid="lead-consent-checkbox"
                  type="checkbox"
                  required
                  aria-required="true"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  <span className="text-rose-600 font-semibold">*</span>{" "}
                  I&rsquo;d like to receive my result and Australian job-search tips
                  from{" "}
                  <a
                    href="https://mattooconsulting.com"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-slate-900 underline-offset-2 hover:underline"
                  >
                    Mattoo Consulting
                  </a>
                  . <span className="text-slate-500">(Required to unlock — we don&rsquo;t spam.)</span>
                </span>
              </label>
              {touched && !consent && (
                <p
                  data-testid="lead-consent-error"
                  className="text-xs text-rose-600 -mt-2 pl-7"
                >
                  Please tick the box above to receive your result.
                </p>
              )}

              <button
                type="submit"
                data-testid="lead-submit-btn"
                disabled={!canSubmit}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white px-6 py-3.5 text-sm font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Unlocking…
                  </>
                ) : (
                  <>
                    Unlock my result <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-[11px] text-slate-500 text-center pt-1">
                We use your email only to send your result and occasional
                career-coaching tips. Unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Unlock;
